import { useState, useEffect } from 'react'
import Navigation from './components/Navigation'
import HeroBanner from './components/HeroBanner'
import CustomerCases from './components/CustomerCases'
import CustomerCasesDetail from './components/CustomerCasesDetail'
import PaymentReports from './components/PaymentReports'
import HomePaymentReports from './components/HomePaymentReports'
import CaseDetail from './components/CaseDetail'
import CaseOnlyDetail from './components/CaseOnlyDetail'
import Footer from './components/Footer'
import { trackCaseEvent, trackReportEvent } from './utils/analytics'
import { API_BASE_URL } from './config'

type Page = 'home' | 'cases-list' | 'reports-list' | 'case-detail'

interface CaseItem {
  id: number
  title: string
  logo: string
  industry: string
  region: string
  industry_name?: string
  region_name?: string
}

interface HomeCaseItem {
  id: number
  name: string
  image: string
}

function App() {
  const [currentPage, setCurrentPage] = useState<Page>('home')
  const [_pageHistory, setPageHistory] = useState<Page[]>(['home'])
  const [casesExpanded, setCasesExpanded] = useState(true)
  const [reportsExpanded, setReportsExpanded] = useState(false)
  const [isUnlocked, setIsUnlocked] = useState(false)
  const [selectedCase, setSelectedCase] = useState<CaseItem | null>(null)
  const [selectedReportId, setSelectedReportId] = useState<number | null>(null)
  const [detailType, setDetailType] = useState<'case' | 'report'>('case')
  const [pdfUrl, setPdfUrl] = useState<string>('')
  const [homeCases, setHomeCases] = useState<HomeCaseItem[]>([])

  useEffect(() => {
    const fetchHomeCases = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/home/cases`)
        const result = await response.json()
        if (result.success) {
          setHomeCases(result.data)
        }
      } catch (error) {
        console.error('Failed to fetch home cases:', error)
      }
    }

    fetchHomeCases()
  }, [])

  const handleCasesToggle = () => {
    if (casesExpanded) {
      setCasesExpanded(false)
    } else {
      setCasesExpanded(true)
      setReportsExpanded(false)
    }
  }

  const handleReportsToggle = () => {
    if (reportsExpanded) {
      setReportsExpanded(false)
    } else {
      setCasesExpanded(false)
      setReportsExpanded(true)
    }
  }

  const handleCasesClick = () => {
    setCurrentPage('cases-list')
    setPageHistory(prev => [...prev, 'cases-list'])
    setIsUnlocked(false)
  }

  const handleReportsClick = () => {
    setCurrentPage('reports-list')
    setPageHistory(prev => [...prev, 'reports-list'])
    setIsUnlocked(false)
  }

  const handleReportClick = (reportId: number) => {
    setCurrentPage('case-detail')
    setPageHistory(prev => [...prev, 'case-detail'])
    setSelectedReportId(reportId)
    setDetailType('report')
    setIsUnlocked(false)
    setPdfUrl('')
    trackReportEvent(reportId, 'view')
  }

  const handleCaseClick = (caseItem: any) => {
    setCurrentPage('case-detail')
    setPageHistory(prev => [...prev, 'case-detail'])
    setSelectedCase({
      id: caseItem.id,
      title: caseItem.title,
      logo: caseItem.logo,
      industry: caseItem.industry_name || caseItem.industry || '',
      region: caseItem.region_name || caseItem.region || ''
    })
    setDetailType('case')
    setIsUnlocked(false)
    setPdfUrl('')
    trackCaseEvent(caseItem.id, 'view')
  }

  const handleHomeCaseClick = (homeCase: HomeCaseItem) => {
    setCurrentPage('case-detail')
    setPageHistory(prev => [...prev, 'case-detail'])
    setSelectedCase({
      id: homeCase.id,
      title: homeCase.name + ' × Antom',
      logo: homeCase.image,
      industry: '',
      region: ''
    })
    setDetailType('case')
    setIsUnlocked(false)
    setPdfUrl('')
    trackCaseEvent(homeCase.id, 'view')
  }

  const handleBack = () => {
    setPageHistory(prev => {
      if (prev.length <= 1) return prev
      const newHistory = prev.slice(0, -1)
      setCurrentPage(newHistory[newHistory.length - 1])
      return newHistory
    })
  }

  const handleHomeClick = () => {
    setCurrentPage('home')
    setPageHistory(['home'])
    setIsUnlocked(false)
    setPdfUrl('')
  }

  const handlePdfUrlLoaded = (url: string) => {
    setPdfUrl(url)
  }

  const handleUnlock = () => {
    setIsUnlocked(true)
  }

  const getNavTitle = () => {
    if (currentPage === 'case-detail') return '顺丰同城 × Antom'
    if (currentPage === 'reports-list') return '支付报告'
    return '资源中心'
  }

  const showNavigation = currentPage !== 'home' && currentPage !== 'cases-list' && currentPage !== 'reports-list' && currentPage !== 'case-detail'

  return (
    <div className="app-container">
      {showNavigation && (
        <Navigation
          title={getNavTitle()}
          showBack={true}
          onBack={handleBack}
        />
      )}
      {currentPage === 'home' ? (
        <>
          <HeroBanner onLogoClick={handleHomeClick} />
          <CustomerCases
            isExpanded={casesExpanded}
            onToggle={handleCasesToggle}
            onCasesClick={handleCasesClick}
            onCaseClick={handleHomeCaseClick}
            cases={homeCases}
          />
          <HomePaymentReports
            isExpanded={reportsExpanded}
            onToggle={handleReportsToggle}
            onReportsClick={handleReportsClick}
            onReportClick={handleReportClick}
          />
          {/* 解决方案暂时隐藏 */}
          {/* <MenuItem
            icon="solution"
            title="解决方案"
            description="为您的业务量身定制"
            backgroundColor="#f7f7f7"
            isExpanded={solutionsExpanded}
            onToggle={handleSolutionsToggle}
          /> */}
        </>
      ) : currentPage === 'cases-list' ? (
        <CustomerCasesDetail onCaseClick={handleCaseClick} />
      ) : currentPage === 'reports-list' ? (
        <PaymentReports onReportClick={handleReportClick} />
      ) : detailType === 'case' ? (
        <CaseOnlyDetail
          caseId={selectedCase?.id}
          onPdfUrlLoaded={handlePdfUrlLoaded}
          isUnlocked={isUnlocked}
          onUnlock={handleUnlock}
        />
      ) : (
        <CaseDetail
          isUnlocked={isUnlocked}
          onUnlock={handleUnlock}
          caseId={selectedReportId ?? undefined}
          type={detailType}
          onPdfUrlLoaded={handlePdfUrlLoaded}
        />
      )}
      <Footer
        isUnlocked={isUnlocked}
        pdfUrl={pdfUrl}
        mode={currentPage}
        caseId={detailType === 'case' ? selectedCase?.id : undefined}
        reportId={detailType === 'report' ? selectedReportId ?? undefined : undefined}
        onBack={handleBack}
      />
    </div>
  )
}

export default App
